import React from 'react'

import { CloudUploadIcon, UploadIcon, XIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'

import DndArea from '~/client/src/desktop/components/DndArea/DndArea'
import DesktopFileInput from '~/client/src/desktop/components/FileInput/DesktopFileInput'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { FilePicker } from '~/client/src/shared/components/FilePicker'
import { Loader } from '~/client/src/shared/components/Loader'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import FileType from '~/client/src/shared/enums/FileType'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { ACCEPTED_SITEMAP_EXTENSIONS } from '../../../MapViewSetUp.store'
import MapViewItemsSetupStore from '../../../stores/MapViewItemsSetup.store'
import SitemapControlStore from '../../../stores/SitemapControl.store'

interface IProps {
  store: SitemapControlStore

  mapViewItemsSetupStore?: MapViewItemsSetupStore
  state?: DesktopInitialState
}

const EXTENSIONS_SEPARATOR = ','

@inject('state')
@observer
export default class UploadWhiteboardDialog extends React.Component<IProps> {
  public render(): JSX.Element {
    const {
      sitemapName,
      isUploading,
      basemapFile,
      basmapFileObjectUrl,
      setSitemapBasemap,
      changeMapName,
      setPdfOrCreateSitemap,
      toggleCreateWhiteboardMenu,
    } = this.props.store

    const isSaveButtonActive = !isUploading && !!sitemapName && !!basemapFile

    return (
      <Dialog
        open={true}
        onOpenChange={open => {
          if (!open) toggleCreateWhiteboardMenu()
        }}
      >
        <DialogContent
          // `!important` overrides beat legacy SCSS specificity for the DnD blocks inside.
          className="tw:sm:max-w-[400px] tw:p-6 tw:[&_.upload-schedule-block]:!w-full tw:[&_.upload-schedule-block]:!min-h-[180px] tw:[&_.general-map-setup-dnd-area-title]:!py-10 tw:[&_.general-map-setup-dnd-area-title]:!px-4 tw:[&_.general-map-setup-dnd-area-title]:!min-h-[140px]"
          // Custom top-left close; suppress shadcn's default top-right X.
          showCloseButton={false}
        >
          <button
            type="button"
            onClick={toggleCreateWhiteboardMenu}
            aria-label={Localization.translator.cancel}
            className="tw:absolute tw:top-2 tw:left-2 tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-md tw:border-0 tw:bg-transparent tw:p-0 tw:text-muted-foreground tw:cursor-pointer tw:outline-none tw:transition-colors tw:hover:bg-muted tw:focus-visible:bg-muted"
          >
            <XIcon className="tw:size-4" aria-hidden="true" />
          </button>
          <DialogTitle className="tw:pl-8 tw:text-title-md tw:text-foreground">
            {Localization.translator.mapFileUpload.uploadWhiteboard}
          </DialogTitle>
          <div className="col">
            <div className="row y-start">
              <div>
                <div className="general-map-setup-dnd-area">
                  {!basemapFile ? (
                    <>
                      <DndArea
                        fileType={FileType.Image}
                        accept={ACCEPTED_SITEMAP_EXTENSIONS}
                        title={this.title}
                        onDrop={setSitemapBasemap}
                        className="upload-schedule-dnd-container col x-center y-center"
                      />
                      {this.renderBrowseComputerLine()}
                    </>
                  ) : (
                    <DesktopFileInput
                      id="sitemap-basemap-input"
                      name={basemapFile.name}
                      value={basmapFileObjectUrl}
                      isReadonly={true}
                      textClassName="hint"
                      shouldHideIconAndOutline={true}
                      projectId={this.props.state.activeProject.id}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="row mt5 relative overflow-hidden">
              <StruxhubInput
                id="sitemap-name-input"
                label={Localization.translator.mapFileUpload.whiteboardName}
                isRequired={true}
                value={sitemapName}
                onChange={changeMapName}
              />
            </div>
            <Button
              variant="default"
              size="lg"
              disabled={!isSaveButtonActive}
              onClick={() => setPdfOrCreateSitemap(true)}
              className="tw:mt-3 tw:w-full"
            >
              {isUploading ? <Loader /> : Localization.translator.save}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  private get title(): string | JSX.Element {
    const { isUploading, whiteboardCreationPdfFile } = this.props.store
    if (isUploading) {
      return (
        <Loader hint={Localization.translator.mapFileUpload.uploadingBasemap} />
      )
    }

    if (whiteboardCreationPdfFile) {
      return Localization.translator.mapFileUpload.basemapUploaded
    }

    return (
      <div className="col text large no-grow x-center">
        <span>
          <CloudUploadIcon size={20} className="tw:align-middle" />{' '}
          {Localization.translator.mapFileUpload.dragMapHere}
        </span>
        <span>
          {Localization.translator.mapFileUpload.acceptedExtensions(
            '.pdf, .png, .jpeg, .jpg',
          )}
        </span>
      </div>
    )
  }

  private renderBrowseComputerLine = (): JSX.Element => {
    const { basemapFile, setSitemapBasemap } = this.props.store
    return (
      <FilePicker
        accept={ACCEPTED_SITEMAP_EXTENSIONS.join(EXTENSIONS_SEPARATOR)}
        onPick={([file]) => setSitemapBasemap(file)}
        render={<Button variant="outline" className="tw:w-full" />}
      >
        <UploadIcon data-icon="inline-start" />
        {basemapFile
          ? Localization.translator.change_verb
          : Localization.translator.uploadFile}
      </FilePicker>
    )
  }
}
